import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import axios from '../../../sharedModules/utils/axios.utils';

const JobExecutionStatusesListPage: React.FC = () => {
  const [statuses, setStatuses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<any>('/admin/job_execution_statuses');
      if (response?.data?.statuses && Array.isArray(response.data.statuses)) {
        setStatuses(response.data.statuses);
      }
    } catch (err: any) {
      setError('Failed to load execution statuses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveStatuses = async (updatedStatuses: string[]) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put('/admin/job_execution_statuses', {
        statuses: updatedStatuses,
      });
      setStatuses(updatedStatuses);
      setSuccess('Execution statuses saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to save execution statuses');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const items = Array.from(statuses);
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    saveStatuses(items);
  };

  const handleMoveDown = (index: number) => {
    if (index === statuses.length - 1) return;
    const items = Array.from(statuses);
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    saveStatuses(items);
  };

  const handleAddStatus = () => {
    setNewStatus('');
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditStatus = (index: number) => {
    setNewStatus(statuses[index]);
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteStatus = (index: number) => {
    if (window.confirm(`Are you sure you want to delete "${statuses[index]}"?`)) {
      const updatedStatuses = statuses.filter((_, i) => i !== index);
      saveStatuses(updatedStatuses);
    }
  };

  const handleSaveDialog = () => {
    if (!newStatus.trim()) {
      alert('Status name cannot be empty');
      return;
    }

    let updatedStatuses: string[];
    if (editingIndex !== null) {
      updatedStatuses = statuses.map((status, i) => (i === editingIndex ? newStatus.trim() : status));
    } else {
      updatedStatuses = [...statuses, newStatus.trim()];
    }

    saveStatuses(updatedStatuses);
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Job Execution Statuses
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddStatus} disabled={saving}>
          Add Status
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the arrow buttons to reorder statuses. These statuses will be available in the Project Communication
            forms.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {statuses.map((status, index) => (
                <ListItem
                  key={index}
                  sx={{
                    mb: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        aria-label="move up"
                        onClick={() => handleMoveUp(index)}
                        disabled={saving || index === 0}
                        size="small"
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="move down"
                        onClick={() => handleMoveDown(index)}
                        disabled={saving || index === statuses.length - 1}
                        size="small"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditStatus(index)}
                        disabled={saving}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteStatus(index)}
                        disabled={saving}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={status}
                    secondary={`Position ${index + 1}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {statuses.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No execution statuses defined. Click "Add Status" to create one.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex !== null ? 'Edit Status' : 'Add New Status'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Status Name"
            fullWidth
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveDialog();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobExecutionStatusesListPage;
